<template>
  <div id="accordionTourTerms" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourTerms" class="accordion-header">
        <button
          aria-controls="collapseTourTerms"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTourTerms"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="template-setup-accordion-icon me-8 d-flex align-items-center justify-content-center">
              <v-icon icon="contact" size="xs" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.termsConditions") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.termsSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourTerms"
        aria-labelledby="headingTourTerms"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourTerms"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <nav class="translations-tab mb-8">
            <div id="nav-tab" class="nav nav-tabs border-bottom-0" role="tablist">
              <v-button
                v-for="(language, index) in languages"
                :id="`nav-template-day-update-basic-information-${language.key}`"
                :key="index"
                :aria-controls="`template-day-update-basic-information-${language.key}`"
                :class="{ active: language.shortName === currentLanguage }"
                :data-bs-target="`#template-day-update-basic-information-${language.key}`"
                aria-selected="true"
                class="nav-tab-link py-4 px-12 me-8 text-xs"
                data-bs-toggle="tab"
                role="tab"
                size="sm"
                variant="outline-primary"
                @click="currentLanguage = language.shortName"
              >
                {{ language.label }}
              </v-button>
            </div>
          </nav>
          <form id="case-tour-update-terms-text-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset v-for="language in languages" :key="language.shortName">
              <div class="form-outline" v-if="currentLanguage === language.shortName">
                <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                  <span>{{ $t("clientTour.termsConditions") }}</span>
                </label>

                <v-html-editor
                  :id="'case-tour-update-terms-text' + language.shortName"
                  ref="case-tour-update-terms-text-ref"
                  :value="termsAndConditions[language.shortName].description"
                  @input="handleInput(language.shortName, $event)"
                  @media-uploaded="editorMedia.push($event)"
                  height="300"
                />
              </div>
            </fieldset>
            <v-button
              :is-loading="accepted"
              :disabled="disableButton"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="updateTemplate"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import Template from "@/api/objects/Template";
import useConcepts from "@/helpers/Concepts";
import { createLocaleTranslationMap, useAppLanguages, useLocaleKeyedRef } from "@/composables/useAppLanguages";
import {mapConcepts, getAppLanguages } from '@/helpers/ConceptHelper';
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

/* --- Types --- */
interface Translation {
  locale: string;
  description: string;
}

/* --- Props & Emits --- */
const props = defineProps<{
  agency: string;
  template: Template;
}>();
const emit = defineEmits(["reloadTemplate"]);

/* --- Toast --- */
const toast = useToast();
const { t } = useI18n();

/* --- Languages & Terms --- */
const concepts = useConcepts();
const languages = computed(() =>
  getAppLanguages().length ? getAppLanguages() : []
);

/* --- Terms and Conditions State --- */
const termsAndConditions = createLocaleTranslationMap(() => ({ description: "", key: "terms_and_conditions" }));

(props.template?.translations || []).forEach((translation: Translation) => {
  if (termsAndConditions[translation.locale]) {
    termsAndConditions[translation.locale].description = translation.description;
  }
});

/* --- Form State --- */
const form = useFormValidation("", termsAndConditions);
const { accepted, toggleAccepted } = form;
const disableButton = ref(true);
const editorMedia = ref<any[]>([]);
const { resolveStoredLocale, defaultLocale } = useAppLanguages();
const currentLanguage = ref(resolveStoredLocale(localStorage.getItem("language")));

/* --- Handlers --- */
const handleInput = (language: string, value: string) => {
  termsAndConditions[language].description = value.trim();
  disableButton.value = Object.values(termsAndConditions).some((item) => item.description === "");
};

const updateTemplate = async () => {
  disableButton.value = true;
  try {
    toggleAccepted();

    const data = {
      name: props.template.name,
      travel_type: props.template.travelType,
      origin: props.template.originId,
      destination: props.template.destinationId,
      contact_text: props.template.contactText,
      complementary_text: props.template.complementaryText,
      translations: termsAndConditions,
    };

    const template = await api.activityService.updateActivity(props.agency, props.template.id, data);

    if (editorMedia.value.length > 0) {
      const editorMediaData = {
        media: editorMedia.value,
        model_type: "template",
        collection: "tinymce",
        model_id: props.template.id,
      };
      await api.media.confirm("s3", editorMediaData);
    }

    toast.success(t("general.shared.savedChanges"), {
        position: "bottom-right",
      });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message, { position: "bottom-right" });
  } finally {
    // disableButton.value = Object.values(termsAndConditions).some((item) => item.description === "") ? true : false;
    toggleAccepted();
  }
};
</script>

<style lang="scss" scoped>
.accordion-item {
  background-color: transparent;
}

.accordion .accordion-item .accordion-button {
  font-size: 1rem;
}

.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.9375rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.8125rem !important;
}

</style>
